import ReportChart from "./ReportChart";
import { VictoryChart, VictoryAxis, VictoryLegend, VictoryLine, VictoryBar, VictoryGroup, VictoryScatter, VictoryContainer } from "victory";
import { abbreviateNumber, colors, monthYearStringToDate } from "../ReportsHelper";
import NoData from "../NoData";
import { ReportChartConsts } from "../ReportTooltips";
import PropTypes from "prop-types";

export default function QuotesNbusByLobChart({ data }) {
    const labelColor = document.documentElement.getAttribute('data-gdstheme') === 'dark' ? "#ffffff" : "#000000";
    let noData = !data?.some(group => group.GroupedData?.length || group.Data?.length);
    if (noData) return <ReportChart title="Quotes & New Business by Line of Business"><NoData /></ReportChart>;

    if (!noData) {
        data.forEach(lobData => {
            lobData.GroupedData?.forEach(monthData => {
                monthData.GroupingKey = monthYearStringToDate(monthData.GroupingKey);
            });
        });
    }

    let useQuarterly = data?.some(group => group.GroupedData?.length > 12);

    const formattedData = data.map((group, index) => {
        const groupedData = useQuarterly
            ? Object.entries(
                group.GroupedData.reduce((acc, item) => {
                    const quarter = `Q${Math.floor(item.GroupingKey.getMonth() / 3) + 1}\n${item.GroupingKey.getFullYear()}`;
                    acc[quarter] = acc[quarter] || { Quotes: 0, NewBusiness: 0 };
                    acc[quarter].Quotes += item.Data.Quotes;
                    acc[quarter].NewBusiness += item.Data.NewBusiness;
                    return acc;
                }, {})
            ).map(([key, value]) => ({
                GroupingKey: key,
                Data: value,
            }))
            : group.GroupedData;

        return {
            key: group.GroupingKey,
            quotes: groupedData.map(item => ({
                x: item.GroupingKey,
                y: item.Data.Quotes,
            })),
            newBusiness: groupedData.map(item => ({
                x: item.GroupingKey,
                y: item.Data.NewBusiness,
            })),
            color: colors[index],
        };
    });

    const maxQuotes = Math.max(...formattedData.flatMap(group => group.quotes.map(item => item.y)));
    const maxNewBusiness = Math.max(...formattedData.flatMap(group => group.newBusiness.map(item => item.y)));
    const scaleFactor = maxQuotes / maxNewBusiness;

    return (
        <ReportChart {...ReportChartConsts('quotesNbusByLob')} big>
            {noData ? <NoData /> :
                <VictoryChart
                    padding={{ bottom: 70, left: 50, right: 50, top: 40 }}
                    domainPadding={{ x: 25 }}
                    containerComponent={
                        <VictoryContainer
                            responsive={true}
                            preserveAspectRatio={"none"}
                        />
                    }
                >
                    <VictoryLegend
                        x={20}
                        y={10}
                        orientation="horizontal"
                        gutter={20}
                        style={{ labels: { fontSize: 10, fill: labelColor } }}
                        data={formattedData.map(group => ({ name: group.key, symbol: { fill: group.color } }))}
                    />
                    <VictoryAxis crossAxis
                        style={{
                            axis: { stroke: labelColor },
                            tickLabels: { fontSize: 10, fill: labelColor },
                        }}
                        tickValues={Array.from(
                            new Set(formattedData.flatMap(group => group.quotes.map(item => item.x)))
                        ).sort((a, b) => new Date(a) - new Date(b))}
                        tickFormat={(x) => {
                            if (useQuarterly) {
                                return x;
                            }
                            const date = new Date(x);
                            return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }).replace(' ', '\n');
                        }}
                    />
                    <VictoryAxis dependentAxis
                        domain={{ y: [0, Math.max(maxQuotes, maxNewBusiness * scaleFactor) * 1.2] }}
                        style={{
                            axis: { stroke: labelColor },
                            tickLabels: {
                                fontSize: 10,
                                fill: labelColor,
                            },
                            grid: {
                                stroke: "#d9d9d9",
                                size: 5,
                            }
                        }}
                        tickFormat={(x) => `${abbreviateNumber(x)}`}
                    />
                    <VictoryAxis dependentAxis
                        orientation="right"
                        style={{
                            tickLabels: {
                                fontSize: 10,
                                fill: labelColor,
                            },
                            axis: { stroke: labelColor },
                            grid: { stroke: "none" },
                        }}
                        tickFormat={(x) => `${abbreviateNumber((x / scaleFactor).toFixed(0))}`}
                    />
                    <VictoryGroup
                        offset={8}
                        style={{ data: { width: 8 } }}
                    >
                        {
                            formattedData.map(d => (
                                <VictoryBar
                                    data={d.quotes}
                                    style={{
                                        data: {
                                            fill: d.color,
                                        },
                                    }}
                                />
                            ))
                        }
                    </VictoryGroup>
                    {
                        formattedData.map(d => (
                            <VictoryLine
                                key={d.key}
                                interpolation="cardinal"
                                data={d.newBusiness.map(item => ({
                                    ...item,
                                    y: item.y * scaleFactor,
                                }))}
                                style={{
                                    data: {
                                        stroke: d.color,
                                    },
                                }}
                            />
                        ))
                    }
                    {
                        formattedData.map(d => (
                            <VictoryScatter
                                size={2}
                                data={d.newBusiness.map(item => ({
                                    ...item,
                                    y: item.y * scaleFactor,
                                }))}
                                style={{
                                    data: {
                                        fill: d.color,
                                    },
                                }}
                            />
                        ))
                    }
                </VictoryChart>
            }
        </ReportChart>
    );
}

QuotesNbusByLobChart.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            GroupingKey: PropTypes.string,
            GroupedData: PropTypes.arrayOf(
                PropTypes.shape({
                    GroupingKey: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
                    Data: PropTypes.shape({
                        Quotes: PropTypes.number,
                        NewBusiness: PropTypes.number,
                    }),
                })
            ),
        })
    ),
};